import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, X } from "lucide-react";

const PRESETS = ["Size", "Material / Paper", "Finishing", "Lamination", "Printing Side", "Corner", "Binding", "Pages", "Colour", "Thickness"];

export const ProductOptionsEditor = ({ options, setOptions }) => {
  const addGroup = (label = "") => setOptions([...options, { label, required: true, choices: [{ name: "", add_on: "" }] }]);
  const updGroup = (i, patch) => setOptions(options.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const rmGroup = (i) => setOptions(options.filter((_, j) => j !== i));
  const addChoice = (i) => updGroup(i, { choices: [...options[i].choices, { name: "", add_on: "" }] });
  const updChoice = (i, k, patch) => updGroup(i, { choices: options[i].choices.map((c, j) => (j === k ? { ...c, ...patch } : c)) });
  const rmChoice = (i, k) => updGroup(i, { choices: options[i].choices.filter((_, j) => j !== k) });
  const used = new Set(options.map((g) => g.label));

  return (
    <div className="space-y-3" data-testid="product-options-editor">
      <p className="text-xs text-muted-foreground">Each option is a dropdown the customer/agent picks when ordering. Every choice can add to the unit price (RM, use 0 for no change or negative for a discount).</p>
      {options.map((g, i) => (
        <div key={i} className="rounded-lg border bg-background overflow-hidden" data-testid={`option-group-${i}`}>
          <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border-b">
            <Input value={g.label} onChange={(e) => updGroup(i, { label: e.target.value })} placeholder="Option title, e.g. Size" className="h-8 font-semibold max-w-xs" data-testid={`option-label-${i}`} />
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto cursor-pointer">
              <Checkbox checked={g.required !== false} onCheckedChange={(v) => updGroup(i, { required: !!v })} data-testid={`option-required-${i}`} /> Required
            </label>
            <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => rmGroup(i)} data-testid={`option-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="p-3 space-y-1.5">
            <div className="grid grid-cols-[1fr_120px_28px] gap-2 text-[11px] text-muted-foreground px-1"><span>Choice</span><span>+ RM / unit</span><span /></div>
            {g.choices.map((c, k) => (
              <div key={k} className="grid grid-cols-[1fr_120px_28px] gap-2 items-center">
                <Input value={c.name} onChange={(e) => updChoice(i, k, { name: e.target.value })} placeholder={k === 0 ? "e.g. A4" : "Another choice"} className="h-8" data-testid={`option-choice-${i}-${k}`} />
                <Input type="number" step="0.01" value={c.add_on} onChange={(e) => updChoice(i, k, { add_on: e.target.value })} placeholder="0.00" className="h-8 font-mono" data-testid={`option-addon-${i}-${k}`} />
                <button type="button" onClick={() => rmChoice(i, k)} className="text-muted-foreground hover:text-rose-600" data-testid={`option-choice-remove-${i}-${k}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => addChoice(i)} data-testid={`option-choice-add-${i}`}><Plus className="w-3 h-3 mr-1" /> Add choice</Button>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-1.5">
        <Button type="button" size="sm" variant="outline" onClick={() => addGroup("")} data-testid="option-group-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add Option</Button>
        <span className="text-[11px] text-muted-foreground ml-1">Quick add:</span>
        {PRESETS.filter((p) => !used.has(p)).map((p) => (
          <button type="button" key={p} onClick={() => addGroup(p)} className="text-[11px] px-2 py-0.5 rounded-full border hover:bg-muted" data-testid={`option-preset-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{p}</button>
        ))}
      </div>
    </div>
  );
};

export const DeliveryPicker = ({ methods, value, onChange, testid = "delivery-method" }) => (
  <div className="grid sm:grid-cols-3 gap-2" data-testid={testid}>
    {methods.map((m) => {
      const active = value === m.name;
      return (
        <button type="button" key={m.name} onClick={() => onChange(m.name)} data-testid={`${testid}-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={`text-left rounded-lg border p-3 transition-colors ${active ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30" : "hover:bg-muted/50"}`}>
          <div className="flex items-center justify-between"><span className="font-semibold text-sm">{m.name}</span><span className="font-mono text-xs">{Number(m.fee) > 0 || Number(m.per_kg) > 0 ? `RM ${Number(m.fee).toFixed(2)}${Number(m.per_kg) > 0 ? ` + RM${Number(m.per_kg).toFixed(2)}/kg` : ""}` : "Free"}</span></div>
          {(m.courier || m.region) && <div className="text-[11px] text-muted-foreground">{[m.courier, m.region].filter(Boolean).join(" • ")}</div>}
          {m.free_above ? <div className="text-[11px] text-emerald-600">Free above RM {Number(m.free_above).toFixed(0)}</div> : null}
          {m.note && <div className="text-[11px] text-muted-foreground mt-0.5">{m.note}</div>}
        </button>
      );
    })}
  </div>
);

export const Field = ({ label, children, hint }) => (
  <div><Label>{label}</Label>{children}{hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}</div>
);
